import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { 
  BookOpen, Trophy, Clock, TrendingUp, Star,
  ChevronRight, AlertCircle, Home, GraduationCap,
  Calendar, Target
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ParentProgressPage() {
  const { shareToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const res = await fetch(`${API_URL}/api/parent/progress/${shareToken}`);
        if (!res.ok) {
          throw new Error("Lien invalide ou expire");
        }
        const json = await res.json();
        setData(json);
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    };
    fetchProgress();
  }, [shareToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="w-16 h-16 bg-indigo-200 rounded-full mx-auto mb-4" />
          <p className="text-slate-500">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Lien invalide</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-500 text-white rounded-xl font-bold hover:bg-indigo-600"
          >
            <Home size={20} />
            Retour a l'accueil
          </Link>
        </div>
      </div>
    );
  }

  const { student_name, niveau, teacher_name, stats, recent_activities } = data;

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 text-indigo-200 text-sm mb-2">
            <GraduationCap size={16} />
            <span>Classe de {teacher_name}</span>
          </div>
          <h1 className="text-3xl font-bold mb-1">Progression de {student_name}</h1>
          <div className="flex items-center gap-4 text-indigo-200">
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-bold">
              Niveau {niveau?.toUpperCase()}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 -mt-4">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-4 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-2">
              <Clock className="text-blue-500" size={24} />
            </div>
            <p className="text-2xl font-bold text-slate-800">{stats.total_reading_minutes}</p>
            <p className="text-sm text-slate-500">min de lecture</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-4 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-2">
              <TrendingUp className="text-green-500" size={24} />
            </div>
            <p className="text-2xl font-bold text-slate-800">{stats.avg_words_per_minute}</p>
            <p className="text-sm text-slate-500">mots/min</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-4 text-center">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-2">
              <Target className="text-orange-500" size={24} />
            </div>
            <p className="text-2xl font-bold text-slate-800">{stats.grammar_exercises_count}</p>
            <p className="text-sm text-slate-500">exercices</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-4 text-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mx-auto mb-2">
              <Star className="text-yellow-500" size={24} />
            </div>
            <p className="text-2xl font-bold text-slate-800">{stats.stars_earned}</p>
            <p className="text-sm text-slate-500">etoiles</p>
          </div>
        </div>

        {/* Recent Activities */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Calendar className="text-indigo-500" size={24} />
            Activites recentes
          </h2>
          
          {/* Reading Sessions */}
          {recent_activities.reading.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-bold text-slate-500 uppercase mb-3">Lecture</h3>
              <div className="space-y-2">
                {recent_activities.reading.map((session, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <BookOpen className="text-blue-500" size={20} />
                      <div>
                        <p className="font-bold text-slate-700">{session.text_title}</p>
                        <p className="text-sm text-slate-500">
                          {session.words_count} mots en {Math.round(session.duration_seconds / 60)} min
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-blue-600">{session.words_per_minute} mots/min</p>
                      <p className="text-xs text-slate-400">
                        {new Date(session.date).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Grammar Exercises */}
          {recent_activities.grammar.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-bold text-slate-500 uppercase mb-3">Grammaire</h3>
              <div className="space-y-2">
                {recent_activities.grammar.map((ex, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Trophy className="text-green-500" size={20} />
                      <p className="font-bold text-slate-700">{ex.type || "Exercice"}</p>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${ex.correct ? "text-green-600" : "text-red-500"}`}>
                        {ex.correct ? "Reussi" : "A revoir"}
                      </p>
                      <p className="text-xs text-slate-400">
                        {new Date(ex.date).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dictation Sessions */}
          {recent_activities.dictation.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-slate-500 uppercase mb-3">Dictee</h3>
              <div className="space-y-2">
                {recent_activities.dictation.map((session, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-purple-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-200 rounded-full flex items-center justify-center">
                        <span className="text-lg">✍️</span>
                      </div>
                      <p className="font-bold text-slate-700">Dictee</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-purple-600">
                        {session.score || 0}% correct
                      </p>
                      <p className="text-xs text-slate-400">
                        {new Date(session.date).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {recent_activities.reading.length === 0 && 
           recent_activities.grammar.length === 0 && 
           recent_activities.dictation.length === 0 && (
            <p className="text-center text-slate-400 py-8">
              Aucune activite recente
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="text-center text-slate-400 text-sm py-4">
          <p>L'ecole de Navi - Suivi de progression</p>
        </div>
      </main>
    </div>
  );
}
